import React, { useEffect, useState, useCallback } from 'react';
import { adminApi, API_URL } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PanelHeader } from './_shared';
import { toast } from 'sonner';
import { Save, Download, FileText, Clock, RefreshCw, Zap, CalendarClock } from 'lucide-react';

const DEFAULT_CONTENT = `Crown & Rose — Feature & Build Request Log
Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
Period: Last 48 Hours — Session Recap

=======================================================
COMPLETED REQUESTS
=======================================================

[DONE] Multilingual SEO/GEO regionalization
  Implemented gap-free hreflang (7-tag: x-default + 6 locales) across all public
  pages. useHreflang.js sets document.documentElement.lang for the active locale.

[DONE] JSON-LD Structured Data injection
  Added Organization, WebSite, SoftwareApplication, Article, and VideoObject
  schema.org nodes with @id anchors, inLanguage per locale, og:locale + 5 alternates.

[DONE] Blog i18n + JSON-LD per locale
  All blog posts carry locale-aware Article JSON-LD. Auto-generated posts include
  FAQ schema and multilingual topic rotation.

[DONE] Trust & Diversity page i18n + JSON-LD
  Full translation coverage across 6 locales (en/fr/de/es/pt/zh) with structured data.

[DONE] AI Blog batch generation admin UI
  "Generate All Locales" shows a per-locale results table with title, slug link, and
  pass/fail icons after completion.

[DONE] Character Arc Visualizer — Convergence Annotation
  Amber dashed ReferenceLine marks the convergence chapter; hover tooltip shows
  character names and convergence score.

[DONE] Character Arc Visualizer — Narrative Peaks layer
  "Narrative peak moments" panel below the chart shows plain-English peak
  descriptions per dimension (Psychological, Action, Emotional, etc.).

[DONE] Coach Actions: "Add scene/beat" with free-text steering
  New "Add a scene or beat" action in Coach Actions dialog. AI inserts 140–280 words
  at the best structural gap. Free-text beat note field lets authors steer the AI.

[DONE] Commit & Rebuild blank page fix
  useNovelBuild.js now receives loadProject correctly. Rebuild runs as async
  background job with tile-grid progress (queued/running/completed/error).

[DONE] Edit menu functions fix
  Find / Find & Replace / Undo / Redo all wired via custom DOM events.
  FindBar.jsx component added for in-editor search.

[DONE] Variable Commit toast advisory
  Toast message shown when committing variable changes, advising that a rebuild
  is needed to propagate changes to the manuscript.

[DONE] Notepad fullscreen toggle
  Writer's Notepad (NotebookPanel) now has an expand/collapse fullscreen button.

[DONE] WIF (Workload Identity Federation) status admin UI
  GET /api/admin/wif-status endpoint + WifStatusCard.jsx in Admin → API Keys.
  Shows env-var health indicators for all 10 WIF/OIDC variables.

[DONE] WIF-Ready backend validation fix
  NativeAnthropicChat / NativeOpenAIChat / NativeGeminiChat no longer crash when
  api_key is None (WIF passthrough mode).

[DONE] LandingLocalizationPanel wired into LandingPanel
  Orphaned component connected to the admin landing page panel.

[DONE] Security fix — Gemini key removed from URL parameters
  Validation calls no longer pass Gemini API keys as URL query parameters.

[DONE] Stripe webhook configuration clarified
  Webhook secret is injected via deployment secrets in production. Local .env is
  intentionally empty. Admin UI shows configured/not-configured status correctly.

=======================================================
IN PROGRESS
=======================================================

[IN PROGRESS] Export user requests to text file (this document)
  Build request log panel in Admin → Operations. Text is editable, uploaded to
  Object Storage, and saved to MongoDB with download links.

=======================================================
BACKLOG / FUTURE TASKS
=======================================================

[P2] Submit sitemap.xml to Google Search Console
  Use Admin → Operations → SEO panel's Sitemap Ping card or submit manually at
  https://search.google.com/search-console.

=======================================================
END OF LOG
=======================================================
`;

export default function RequestLogPanel() {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const data = await adminApi.listRequestLogs();
      setLogs(data.logs || []);
    } catch {
      // no-op — logs might not exist yet
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSave = async () => {
    if (!content.trim()) {
      toast.error('Content cannot be empty.');
      return;
    }
    setBusy(true);
    try {
      const entry = await adminApi.saveRequestLog({
        title: title.trim() || undefined,
        content,
      });
      toast.success(`Saved: ${entry.title}`);
      setTitle('');
      await fetchLogs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed — check object storage.');
    } finally {
      setBusy(false);
    }
  };

  const handleSnapshot = async () => {
    setSnapshotBusy(true);
    try {
      await adminApi.triggerLogSnapshot();
      toast.success('Snapshot enqueued — refreshing in 3 s…');
      setTimeout(fetchLogs, 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Snapshot failed.');
    } finally {
      setSnapshotBusy(false);
    }
  };

  const downloadUrl = (logId) =>
    `${API_URL}/admin/request-log/${logId}/download`;

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '';
    return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
  };

  return (
    <section>
      <PanelHeader
        title="Build Request Log"
        subtitle="Compile, edit, and save a permanent record of your feature and build requests. Each save is uploaded to Object Storage and linked from the table below."
        onRefresh={fetchLogs}
      />

      {/* ── Editor ─────────────────────────────────────────────── */}
      <div className="space-y-3 rounded-md border border-border bg-card p-4">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-[11.5px] font-semibold text-muted-foreground mb-1 block">
              Log title (optional)
            </label>
            <Input
              data-testid="request-log-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Session recap — Feb 2026"
              className="h-8 text-[12px]"
            />
          </div>
          <Button
            data-testid="request-log-save-btn"
            size="sm"
            onClick={handleSave}
            disabled={busy}
            className="h-8 gap-1.5 shrink-0"
          >
            <Save className="h-3.5 w-3.5" />
            {busy ? 'Saving…' : 'Save to Database'}
          </Button>
        </div>

        <div>
          <label className="text-[11.5px] font-semibold text-muted-foreground mb-1 block">
            Log content
          </label>
          <Textarea
            data-testid="request-log-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={28}
            className="font-mono text-[11.5px] resize-y"
            placeholder="Paste or type your feature/build requests here…"
          />
        </div>
        <p className="text-[10.5px] text-muted-foreground">
          {content.length.toLocaleString()} characters · pre-populated with your last session's request history
        </p>
      </div>

      {/* ── Auto-snapshot info bar ─────────────────────────────── */}
      <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2.5">
        <CalendarClock className="h-4 w-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-medium">Weekly auto-snapshot</p>
          <p className="text-[11px] text-muted-foreground">
            An activity snapshot (blog posts, new looms, user count, sitemap pings) is
            generated and saved automatically <strong>every Monday</strong> via the
            weekly-ai-digest cron — no action needed.
          </p>
        </div>
        <Button
          data-testid="request-log-snapshot-btn"
          size="sm"
          variant="outline"
          onClick={handleSnapshot}
          disabled={snapshotBusy}
          className="h-8 gap-1.5 shrink-0"
        >
          {snapshotBusy
            ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            : <Zap className="h-3.5 w-3.5" />}
          {snapshotBusy ? 'Generating…' : 'Generate Snapshot Now'}
        </Button>
      </div>

      {/* ── Saved logs table ───────────────────────────────────── */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-[13px] font-semibold">Saved logs</span>
          {loadingLogs && <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground ml-1" />}
        </div>

        {logs.length === 0 && !loadingLogs && (
          <p className="text-[12px] text-muted-foreground py-4 text-center border border-dashed border-border rounded-md">
            No saved logs yet — use the editor above to save your first log.
          </p>
        )}

        {logs.length > 0 && (
          <div
            data-testid="request-log-table"
            className="rounded-md border border-border overflow-hidden"
          >
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-3 py-2 font-semibold text-muted-foreground w-1/2">Title</th>
                  <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Saved</th>
                  <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Size</th>
                  <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Source</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground">Download</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                    <td className="px-3 py-2 font-medium truncate max-w-[260px]" title={log.title}>
                      {log.title}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {fmtDate(log.created_at)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{fmtSize(log.size_bytes)}</td>
                    <td className="px-3 py-2">
                      {log.actor === 'cron:weekly-digest'
                        ? <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9.5px] font-medium text-primary">
                            <CalendarClock className="h-2.5 w-2.5" />cron
                          </span>
                        : <span className="text-[10.5px] text-muted-foreground">{log.actor || 'admin'}</span>
                      }
                    </td>
                    <td className="px-3 py-2 text-right">
                      <a
                        href={downloadUrl(log.id)}
                        download={log.filename}
                        data-testid={`request-log-download-${log.id}`}
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        <Download className="h-3.5 w-3.5" />
                        .txt
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
